import React, { useEffect, useRef, useState } from "react";
import "../../App.css";
import CardGrid from "../CardGrid.js";
import { SERVER_URL, SERVER_URL_2 } from "../../config/config.js";
import { apiFetch } from "../../utils/apiUtils.js";
import { toast } from "react-toastify";

const MIS_GODOWN = "GJAHD02";

const getTodayInputDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatDateForApi = (inputDate) => {
  if (!inputDate) return "";

  const [year, month, day] = inputDate.split("-");
  return `${day}-${month}-${year}`;
};

const formatDateForSoAutomationApi = (inputDate) => {
  if (!inputDate) return "";

  const [year, month, day] = inputDate.split("-");
  return `${day}.${month}.${year}`;
};

const getDownloadFileName = (response) => {
  const disposition = response.headers.get("content-disposition") || "";
  const encodedMatch = disposition.match(/filename\*=utf-8''([^;]+)/i);

  if (encodedMatch?.[1]) {
    return decodeURIComponent(encodedMatch[1]);
  }

  const plainMatch = disposition.match(/filename="?([^"]+)"?/i);
  return plainMatch?.[1] || `mis-report-${Date.now()}.xlsx`;
};

const getInitialLrAutomationState = () => ({
  status: "IDLE",
  total: 0,
  processed: 0,
  success: 0,
  failed: 0,
  message: "",
  isComplete: false,
  hasStarted: false,
  failed_records: [],
});

const isAlreadyRunningMessage = (message) =>
  typeof message === "string" &&
  (/automation already running/i.test(message) ||
    /automation is already running/i.test(message));

const getFriendlyLrAutomationMessage = (message) => {
  if (!message) return "";

  if (isAlreadyRunningMessage(message)) {
    return message.replace(
      /automation already running/i,
      "LR automation is already running",
    );
  }

  return message;
};

const getNormalizedLrAutomationStatus = (status, message) => {
  const normalizedStatus = String(status || "").toUpperCase();

  if (isAlreadyRunningMessage(message)) {
    return "ALREADY_RUNNING";
  }

  if (normalizedStatus === "ERROR") {
    return "FAILED";
  }

  return normalizedStatus || "IDLE";
};

const getInitialSoAutomationState = () => ({
  status: "IDLE",
  total: 0,
  logs: [],
  message: "",
  isComplete: false,
  hasStarted: false,
});

export default function Home({ sessionObject }) {
  const [uniqueStations, setUniqueStations] = useState([]);
  const [stationWeights, setStationWeights] = useState({});
  const [isAutomationLoading, setIsAutomationLoading] = useState(false);
  const [reportUrl, setReportUrl] = useState(null);
  const [isMisReportLoading, setIsMisReportLoading] = useState(false);
  const [isMisModalOpen, setIsMisModalOpen] = useState(false);
  const [misFilters, setMisFilters] = useState({
    fromDate: getTodayInputDate(),
    toDate: getTodayInputDate(),
  });
  const [isLrReportLoading, setIsLrReportLoading] = useState(false);
  const [isStoppingLrAutomation, setIsStoppingLrAutomation] = useState(false);
  const [isLrModalOpen, setIsLrModalOpen] = useState(false);
  const [lrReportUrl, setLrReportUrl] = useState(null);
  const [lrFilters, setLrFilters] = useState({
    fromDate: getTodayInputDate(),
    toDate: getTodayInputDate(),
  });
  const [misResultModal, setMisResultModal] = useState({
    isOpen: false,
    type: "success",
    title: "",
    message: "",
  });
  const [isLrStatusModalOpen, setIsLrStatusModalOpen] = useState(false);
  const [lrAutomationState, setLrAutomationState] = useState(
    getInitialLrAutomationState(),
  );
  const [isSoModalOpen, setIsSoModalOpen] = useState(false);
  const [isSoAutomationLoading, setIsSoAutomationLoading] = useState(false);
  const [isSoStatusModalOpen, setIsSoStatusModalOpen] = useState(false);
  const [soFilters, setSoFilters] = useState({
    fromDate: getTodayInputDate(),
    toDate: getTodayInputDate(),
    file: null,
  });
  const [isMergeMisModalOpen, setIsMergeMisModalOpen] = useState(false);
  const [isMergeMisLoading, setIsMergeMisLoading] = useState(false);

  const [mergeMisFiles, setMergeMisFiles] = useState({
    misFile: null,
    safFile: null,
    resFile: null,
  });  
  const [soAutomationState, setSoAutomationState] = useState(
    getInitialSoAutomationState(),
  );
  const normalizedLrAutomationStatus = getNormalizedLrAutomationStatus(
    lrAutomationState.status,
    lrAutomationState.message,
  );
  const normalizedSoAutomationStatus = String(
    soAutomationState.status || "IDLE",
  ).toUpperCase();
  const canStopLrAutomation =
    !isStoppingLrAutomation &&
    (isLrReportLoading || normalizedLrAutomationStatus === "ALREADY_RUNNING");

  useEffect(() => {
    const payload = {
      date_dict: {
        date_from: `20${
          sessionObject.sessionVariables.financial_year.split("-")[0]
        }-04-01T00:00:01.001Z`,
        date_to: new Date().toISOString(),
      },
      filter_fields: {
        created_from: String(sessionObject.sessionVariables.branch_id),
        owned_by: String(sessionObject.sessionVariables.branch_id),
        companyId: "1",
        fyear: sessionObject.sessionVariables.financial_year,
        mr_no: null,
        crossing_outward: null,
      },
    };

    apiFetch(
      `${SERVER_URL}/report`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
      sessionObject,
    )
      .then((response) => {
        if (!response.ok)
          throw new Error("Network response was not ok " + response.statusText);
        return response.json();
      })
      .then((data) => extractUniqueStations(data.data))
      .catch((error) =>
        console.error("There was a problem with the fetch operation:", error),
      );
  }, []);

  useEffect(() => {
    return () => {
      if (misJobPollRef.current) {
        clearTimeout(misJobPollRef.current);
      }
    };
  }, []);

  const extractUniqueStations = (data) => {
    const branchName = sessionObject.sessionVariables.branch_name;
    const stationSet = new Set();
    const weights = {};

    data.forEach((item) => {
      if (item.station_from_name === branchName) {
        const station = item.station_to_name;
        stationSet.add(station);
        weights[station] = (weights[station] || 0) + (item.charge_weight || 0);
      }
    });

    const sortedStations = Array.from(stationSet).sort();
    const sortedWeights = {};
    sortedStations.forEach(
      (station) => (sortedWeights[station] = `${weights[station]} kg`),
    );

    setUniqueStations(sortedStations);
    setStationWeights(sortedWeights);
  };

  const handleTriggerAutomation = async () => {
    if (isAutomationLoading) return;

    setIsAutomationLoading(true);

    toast.info(
      "Automation started in background. You can continue your work.",
      { autoClose: 3000 },
    );

    try {
      const response = await fetch(
        `${SERVER_URL}/automation/trigger-automation`,
        {
          method: "POST",
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: Number(sessionObject.sessionVariables.user_id),
          }),
        },
      );

      if (!response.ok) throw new Error("API failed");

      const data = await response.json();

      if (data.status === "success") {
        toast.success("Automation completed successfully!", {
          autoClose: 5000,
        });

        setReportUrl(`${SERVER_URL}${data.report_url}`);
      } else {
        toast.error("Automation did not complete properly.", {
          autoClose: 5000,
        });
      }
    } catch (err) {
      console.error(err);
      toast.error("Error triggering automation.", { autoClose: 5000 });
    } finally {
      setIsAutomationLoading(false);
    }
  };

  const handleOpenMergeMisModal = () => {
    console.log("Opening Merge MIS Modal ...");
    if (isMergeMisLoading) return;

    setMergeMisFiles({
      misFile: null,
      safFile: null,
      resFile: null,
    });

    setIsMergeMisModalOpen(true);
  };

  const handleCloseMergeMisModal = () => {
    setIsMergeMisModalOpen(false);
  };

  useEffect(() => {
  console.log("Modal:", isMergeMisModalOpen);
}, [isMergeMisModalOpen]);

  const handleOpenMisModal = () => {
    if (isMisReportLoading) return;

    setMisFilters({
      fromDate: getTodayInputDate(),
      toDate: getTodayInputDate(),
    });
    setIsMisModalOpen(true);
  };

  const handleCloseMisModal = () => {
    setIsMisModalOpen(false);
  };

  const handleMisFilterChange = (event) => {
    const { name, value } = event.target;
    setMisFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  const handleOpenLrModal = () => {
    if (isLrReportLoading) {
      setIsLrStatusModalOpen(true);
      return;
    }

    setLrFilters({
      fromDate: getTodayInputDate(),
      toDate: getTodayInputDate(),
    });
    setIsLrModalOpen(true);
  };

  const handleCloseLrModal = () => {
    setIsLrModalOpen(false);
  };

  const handleLrFilterChange = (event) => {
    const { name, value } = event.target;
    setLrFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  const closeLrStatusModal = () => {
    setIsLrStatusModalOpen(false);
  };

  const handleOpenSoModal = () => {
    if (isSoAutomationLoading) {
      setIsSoStatusModalOpen(true);
      return;
    }

    setSoFilters({
      fromDate: getTodayInputDate(),
      toDate: getTodayInputDate(),
      file: null,
    });
    setIsSoModalOpen(true);
  };

  const handleCloseSoModal = () => {
    setIsSoModalOpen(false);
  };

  const closeSoStatusModal = () => {
    setIsSoStatusModalOpen(false);
  };

  const handleSoFilterChange = (event) => {
    const { name, value, files } = event.target;

    setSoFilters((prevFilters) => ({
      ...prevFilters,
      [name]: name === "file" ? files?.[0] || null : value,
    }));
  };

  const getAbsoluteLrReportUrl = (url) => {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    return `${SERVER_URL}${url}`;
  };

  const openLrReport = () => {
    if (!lrReportUrl) return;
    window.open(lrReportUrl, "_blank", "noopener,noreferrer");
  };

  const handleStopLrAutomation = async () => {
    if (!canStopLrAutomation) return;

    setIsStoppingLrAutomation(true);

    try {
      const response = await fetch(`${SERVER_URL}/automation/stop-lr`, {
        method: "POST",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      updateLrAutomationState({
        status: data?.status || "ERROR",
        message: data?.message || "Failed to stop LR automation.",
        isComplete:
          String(data?.status || "").toUpperCase() === "ERROR" &&
          data?.message === "No automation is currently running.",
      });
    } catch (error) {
      console.error(error);
      updateLrAutomationState({
        status: "ERROR",
        message: error.message || "Error stopping LR automation.",
      });
    } finally {
      setIsStoppingLrAutomation(false);
    }
  };

  const updateLrAutomationState = (payload) => {
    if (!payload || typeof payload !== "object") return;

    if (payload.report_url) {
      setLrReportUrl(getAbsoluteLrReportUrl(payload.report_url));
    }

    setLrAutomationState((prevState) => {
      const nextMessage = getFriendlyLrAutomationMessage(
        payload.message !== undefined && payload.message !== null
          ? payload.message
          : prevState.message,
      );
      const rawStatus = payload.status || prevState.status;
      const nextStatus = isAlreadyRunningMessage(nextMessage)
        ? "ALREADY_RUNNING"
        : rawStatus;
      const normalizedStatus = String(nextStatus).toUpperCase();
      const isComplete =
        payload.isComplete ??
        [
          "COMPLETED",
          "COMPLETED_WITH_ERRORS",
          "FAILED",
          "SUCCESS",
          "ALREADY_RUNNING",
        ].includes(normalizedStatus);

      return {
        ...prevState,
        ...payload,
        status: nextStatus,
        total:
          payload.total !== undefined && payload.total !== null
            ? payload.total
            : prevState.total,
        processed:
          payload.processed !== undefined && payload.processed !== null
            ? payload.processed
            : prevState.processed,
        success:
          payload.success !== undefined && payload.success !== null
            ? payload.success
            : prevState.success,
        failed:
          payload.failed !== undefined && payload.failed !== null
            ? payload.failed
            : prevState.failed,
        failed_records:
          payload.failed_records !== undefined
            ? payload.failed_records
            : prevState.failed_records,
        message: nextMessage,
        hasStarted: true,
        isComplete,
      };
    });
  };

  const consumeLrAutomationStream = async (response) => {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.toLowerCase().includes("application/json")) {
      const data = await response.json();
      updateLrAutomationState({
        ...data,
        isComplete: true,
      });
      return;
    }

    if (!response.body) {
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let receivedStreamEvent = false;

    const handleStreamLine = (line) => {
      const trimmedLine = line.trim();

      if (!trimmedLine.startsWith("data:")) return;
      receivedStreamEvent = true;

      const dataString = trimmedLine.replace(/^data:\s*/, "").trim();

      if (!dataString || dataString === "[DONE]") return;

      try {
        const parsedData = JSON.parse(dataString);
        updateLrAutomationState(parsedData);
      } catch (error) {
        console.error("Failed to parse LR automation stream line:", error);
      }
    };

    while (true) {
      const { value, done } = await reader.read();
      buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop() || "";
      lines.forEach(handleStreamLine);

      if (done) {
        buffer += decoder.decode();
        if (buffer.trim()) {
          handleStreamLine(buffer);

          if (!receivedStreamEvent) {
            try {
              const parsedData = JSON.parse(buffer.trim());
              updateLrAutomationState({
                ...parsedData,
                isComplete: true,
              });
            } catch (error) {
              console.error("Failed to parse LR automation response:", error);
            }
          }
        }
        break;
      }
    }
  };

  const updateSoAutomationState = (payload) => {
    if (!payload || typeof payload !== "object") return;

    setSoAutomationState((prevState) => {
      const nextStatus = String(payload.status || prevState.status || "IDLE").toUpperCase();
      const nextLogs =
        payload.log && !["STARTED", "RUNNING", "SUCCESS", "FAILED", "ERROR"].includes(payload.log)
          ? [...prevState.logs, payload.log]
          : payload.log
            ? [...prevState.logs, payload.log]
            : prevState.logs;
      const nextMessage =
        payload.message ||
        payload.detail ||
        payload.log ||
        (nextStatus === "STARTED"
          ? "SO automation started."
          : nextStatus === "SUCCESS"
            ? "SO automation completed successfully."
            : prevState.message);

      return {
        ...prevState,
        ...payload,
        status: nextStatus,
        total:
          payload.total !== undefined && payload.total !== null
            ? payload.total
            : prevState.total,
        logs: nextLogs,
        message: nextMessage,
        hasStarted: true,
        isComplete:
          payload.isComplete ?? ["SUCCESS", "FAILED", "ERROR"].includes(nextStatus),
      };
    });
  };

  const consumeSoAutomationStream = async (response) => {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.toLowerCase().includes("application/json")) {
      const data = await response.json();
      updateSoAutomationState({
        ...data,
        isComplete: true,
      });
      return;
    }

    if (!response.body) {
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let receivedStreamEvent = false;

    const handleStreamLine = (line) => {
      const trimmedLine = line.trim();

      if (!trimmedLine.startsWith("data:")) return;
      receivedStreamEvent = true;

      const dataString = trimmedLine.replace(/^data:\s*/, "").trim();

      if (!dataString || dataString === "[DONE]") return;

      try {
        const parsedData = JSON.parse(dataString);
        updateSoAutomationState(parsedData);
      } catch (error) {
        console.error("Failed to parse SO automation stream line:", error);
      }
    };

    while (true) {
      const { value, done } = await reader.read();
      buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop() || "";
      lines.forEach(handleStreamLine);

      if (done) {
        buffer += decoder.decode();
        if (buffer.trim()) {
          handleStreamLine(buffer);

          if (!receivedStreamEvent) {
            try {
              const parsedData = JSON.parse(buffer.trim());
              updateSoAutomationState({
                ...parsedData,
                isComplete: true,
              });
            } catch (error) {
              console.error("Failed to parse SO automation response:", error);
            }
          }
        }
        break;
      }
    }
  };

  const openMisResultModal = (type, title, message) => {
    setMisResultModal({
      isOpen: true,
      type,
      title,
      message,
    });
  };

  const closeMisResultModal = () => {
    setMisResultModal((prevState) => ({
      ...prevState,
      isOpen: false,
    }));
  };

  

  const misJobPollRef = useRef(null);

  const handleTriggerMisReport = async () => {
    if (isMisReportLoading) return;

    const { fromDate, toDate } = misFilters;

    if (!fromDate || !toDate) {
      toast.error("Select both From Date and To Date.", { autoClose: 4000 });
      return;
    }

    if (fromDate > toDate) {
      toast.error("From Date cannot be greater than To Date.", {
        autoClose: 4000,
      });
      return;
    }

    setIsMisReportLoading(true);

    try {
      const response = await fetch(
        `${SERVER_URL_2}/automation/trigger-mis-report`,
        {
          method: "POST",
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            from_date: formatDateForApi(fromDate),
            to_date: formatDateForApi(toDate),
            godown: MIS_GODOWN,
            compare_source: "",
          }),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to start MIS report generation.");
      }

      const { job_id } = await response.json();

      openMisResultModal(
        "info",
        "Generating MIS Report",
        `MIS generation started (Job: ${job_id.slice(0, 8)}...). Please wait...`,
      );

      const poll = async () => {
        try {
          const statusRes = await fetch(
            `${SERVER_URL_2}/automation/mis-report-status/${job_id}`,
          );

          if (!statusRes.ok) {
            throw new Error("Failed to check MIS report status.");
          }

          const statusData = await statusRes.json();

          if (statusData.status === "SUCCESS") {
            setIsMisReportLoading(false);
            setIsMisModalOpen(false);

            const downloadRes = await fetch(
              `${SERVER_URL_2}/automation/mis-report-download/${job_id}`,
            );

            if (!downloadRes.ok) {
              throw new Error("Failed to download MIS report.");
            }

            const blob = await downloadRes.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = downloadUrl;
            link.download = getDownloadFileName(downloadRes);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);

            openMisResultModal(
              "success",
              "MIS Report Generated",
              "MIS report downloaded successfully.",
            );
            return;
          }

          if (statusData.status === "FAILED") {
            setIsMisReportLoading(false);
            openMisResultModal(
              "error",
              "MIS Report Failed",
              statusData.error || "MIS generation failed.",
            );
            return;
          }

          openMisResultModal(
            "info",
            "Generating MIS Report",
            `Processing... ${statusData.filename ? `File: ${statusData.filename}` : "Please wait."}`,
          );

          misJobPollRef.current = setTimeout(poll, 300000);
        } catch (error) {
          setIsMisReportLoading(false);
          console.error(error);
          openMisResultModal(
            "error",
            "MIS Report Failed",
            error.message || "Error checking MIS report status.",
          );
        }
      };

      misJobPollRef.current = setTimeout(poll, 3000);
    } catch (error) {
      setIsMisReportLoading(false);
      console.error(error);
      openMisResultModal(
        "error",
        "MIS Report Failed",
        error.message || "Error generating MIS report.",
      );
    }
  };

  const handleTriggerLrReport = async () => {
    if (isLrReportLoading) {
      setIsLrStatusModalOpen(true);
      return;
    }

    const fromDate = lrFilters.fromDate || getTodayInputDate();
    const toDate = lrFilters.toDate || getTodayInputDate();

    if (fromDate > toDate) {
      toast.error("From Date cannot be greater than To Date.", {
        autoClose: 4000,
      });
      return;
    }

    setIsLrReportLoading(true);
    setIsLrModalOpen(false);
    setIsLrStatusModalOpen(true);
    setLrReportUrl(null);
    setLrAutomationState({
      ...getInitialLrAutomationState(),
      status: "STARTING",
      message: "We are processing your LR automation request. Please wait.",
      hasStarted: true,
    });

    try {
      const response = await fetch(
        `${SERVER_URL}/automation/trigger-lr-automation`,
        {
          method: "POST",
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            from_date: fromDate,
            to_date: toDate,
          }),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to trigger LR automation.");
      }
      await consumeLrAutomationStream(response);

      setLrAutomationState((prevState) => ({
        ...prevState,
        status:
          prevState.isComplete || isAlreadyRunningMessage(prevState.message)
            ? prevState.status
            : prevState.failed > 0
              ? "COMPLETED_WITH_ERRORS"
              : "COMPLETED",
        message: prevState.isComplete
          ? prevState.message
          : prevState.failed > 0
            ? "LR automation completed with some failures."
            : "LR automation completed successfully.",
        isComplete: true,
        hasStarted: true,
      }));
    } catch (error) {
      console.error(error);
      setLrAutomationState((prevState) => ({
        ...prevState,
        status: "FAILED",
        message: error.message || "Error triggering LR automation.",
        isComplete: true,
        hasStarted: true,
      }));
      setIsLrStatusModalOpen(true);
    } finally {
      setIsLrReportLoading(false);
    }
  };

  const handleTriggerSoAutomation = async () => {
    if (isSoAutomationLoading) {
      setIsSoStatusModalOpen(true);
      return;
    }

    if (!soFilters.fromDate || !soFilters.toDate) {
      toast.error("Select both From Date and To Date.", { autoClose: 4000 });
      return;
    }

    if (soFilters.fromDate > soFilters.toDate) {
      toast.error("From Date cannot be greater than To Date.", {
        autoClose: 4000,
      });
      return;
    }

    if (!soFilters.file) {
      toast.error("Select an Excel file.", { autoClose: 4000 });
      return;
    }

    setIsSoAutomationLoading(true);
    setIsSoModalOpen(false);
    setIsSoStatusModalOpen(true);
    setSoAutomationState({
      ...getInitialSoAutomationState(),
      status: "STARTING",
      message: "We are processing your SO automation request. Please wait.",
      hasStarted: true,
    });

    try {
      const formData = new FormData();
      formData.append("file", soFilters.file);
      formData.append("from_date", formatDateForSoAutomationApi(soFilters.fromDate));
      formData.append("to_date", formatDateForSoAutomationApi(soFilters.toDate));
      formData.append(
        "target_date",
        formatDateForSoAutomationApi(soFilters.toDate),
      );

      const response = await fetch(`${SERVER_URL}/automation/so-automation`, {
        method: "POST",
        headers: {
          accept: "application/json",
        },
        body: formData,
      });

      if (!response.ok) {
        let errorMessage = "Failed to trigger SO automation.";

        try {
          const errorData = await response.json();
          errorMessage =
            errorData?.detail || errorData?.message || errorMessage;
        } catch (error) {
          const errorText = await response.text();
          errorMessage = errorText || errorMessage;
        }

        throw new Error(errorMessage);
      }

      await consumeSoAutomationStream(response);

      setSoAutomationState((prevState) => ({
        ...prevState,
        status: prevState.isComplete ? prevState.status : "SUCCESS",
        message: prevState.isComplete
          ? prevState.message
          : "SO automation completed successfully.",
        isComplete: true,
        hasStarted: true,
      }));
    } catch (error) {
      console.error(error);
      setSoAutomationState((prevState) => ({
        ...prevState,
        status: "FAILED",
        message: error.message || "Error triggering SO automation.",
        isComplete: true,
        hasStarted: true,
      }));
      setIsSoStatusModalOpen(true);
    } finally {
      setIsSoAutomationLoading(false);
    }
  };

  const handleMergeMisUpload = async () => {
    if (!mergeMisFiles.misFile) {
      toast.error("Please select MIS Report File.");
      return;
    }

    setIsMergeMisLoading(true);

    try {
      const formData = new FormData();

      formData.append("mis_file", mergeMisFiles.misFile);

      if (mergeMisFiles.safFile) {
        formData.append("saf_file", mergeMisFiles.safFile);
      }

      if (mergeMisFiles.resFile) {
        formData.append("res_file", mergeMisFiles.resFile);
      }

      const response = await apiFetch(
        `${SERVER_URL}/report/update_actual_delivery_date`,
        {
          method: "POST",
          body: formData,
        },
        sessionObject
      );

      if (!response.ok) {
        throw new Error("Failed to generate merged report.");
      }

      // Download excel
      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "Merged_MIS_Report.xlsx";

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Merged MIS Report downloaded successfully.");

      setMergeMisFiles({
        misFile: null,
        safFile: null,
        resFile: null,
      });

      setIsMergeMisModalOpen(false);

    } catch (err) {
      console.error(err);
      toast.error(err.message || "Upload failed.");
    } finally {
      setIsMergeMisLoading(false);
    }
  };

  return (
    <div className="home">
      <CardGrid
        onTriggerAutomation={handleTriggerAutomation}
        isAutomationLoading={isAutomationLoading}
        reportUrl={reportUrl}
        onTriggerMisReport={handleOpenMisModal}
        isMisReportLoading={isMisReportLoading}
        onTriggerLrReport={handleOpenLrModal}
        isLrReportLoading={isLrReportLoading}
        lrReportUrl={lrReportUrl}
        onTriggerSoAutomation={handleOpenSoModal}
        isSoAutomationLoading={isSoAutomationLoading}
        onTriggerMergeMis={handleOpenMergeMisModal}
        isMergeMisLoading={isMergeMisLoading}
      />

      {isMisModalOpen && (
        <div className="mis-report-modal-overlay">
          <div className="mis-report-modal">
            <div className="mis-report-modal-title">Trigger MIS Report</div>
            <div className="mis-report-modal-subtitle">
              Select date range. Report generation can take a while.
            </div>

            <label className="mis-report-field">
              <span>From Date</span>
              <input
                type="date"
                name="fromDate"
                value={misFilters.fromDate}
                onChange={handleMisFilterChange}
                disabled={isMisReportLoading}
              />
            </label>

            <label className="mis-report-field">
              <span>To Date</span>
              <input
                type="date"
                name="toDate"
                value={misFilters.toDate}
                onChange={handleMisFilterChange}
                disabled={isMisReportLoading}
              />
            </label>

            <div className="mis-report-modal-actions">
              {isMisReportLoading ? (
                <button
                  type="button"
                  className="mis-report-secondary-btn"
                  onClick={handleCloseMisModal}
                >
                  Hide
                </button>
              ) : (
                <button
                  type="button"
                  className="mis-report-secondary-btn"
                  onClick={handleCloseMisModal}
                >
                  Cancel
                </button>
              )}
              <button
                type="button"
                className="mis-report-primary-btn"
                onClick={handleTriggerMisReport}
                disabled={isMisReportLoading}
              >
                {isMisReportLoading ? "Generating..." : "Generate Report"}
              </button>
            </div>
          </div>
        </div>
      )}

      {misResultModal.isOpen && (
        <div className="mis-report-modal-overlay">
          <div className="mis-report-modal mis-report-result-modal">
            <button
              type="button"
              className="mis-report-close-btn"
              onClick={closeMisResultModal}
            >
              x
            </button>
            <div
              className={`mis-report-result-badge mis-report-result-${misResultModal.type}`}
            >
              {misResultModal.type === "success"
                ? "Success"
                : misResultModal.type === "info"
                  ? "Processing"
                  : "Failed"}
            </div>
            <div className="mis-report-modal-title">{misResultModal.title}</div>
            <div className="mis-report-modal-subtitle">
              {misResultModal.message}
            </div>

            <div className="mis-report-modal-actions">
              {lrReportUrl && lrAutomationState.isComplete && (
                <button
                  type="button"
                  className="mis-report-secondary-btn"
                  onClick={openLrReport}
                >
                  View Report
                </button>
              )}
              <button
                type="button"
                className="mis-report-primary-btn"
                onClick={closeMisResultModal}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {isLrModalOpen && (
        <div className="mis-report-modal-overlay">
          <div className="mis-report-modal">
            <div className="mis-report-modal-title">Trigger LR Automation</div>
            <div className="mis-report-modal-subtitle">
              Select date range for LR automation.
            </div>

            <label className="mis-report-field">
              <span>From Date</span>
              <input
                type="date"
                name="fromDate"
                value={lrFilters.fromDate}
                onChange={handleLrFilterChange}
                disabled={isLrReportLoading}
              />
            </label>

            <label className="mis-report-field">
              <span>To Date</span>
              <input
                type="date"
                name="toDate"
                value={lrFilters.toDate}
                onChange={handleLrFilterChange}
                disabled={isLrReportLoading}
              />
            </label>

            <div className="mis-report-modal-actions">
              <button
                type="button"
                className="mis-report-secondary-btn"
                onClick={handleCloseLrModal}
                disabled={isLrReportLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="mis-report-primary-btn"
                onClick={handleTriggerLrReport}
                disabled={isLrReportLoading}
              >
                {isLrReportLoading ? "Processing..." : "Trigger LR"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isLrStatusModalOpen && (
        <div className="mis-report-modal-overlay">
          <div className="mis-report-modal mis-report-result-modal">
            <button
              type="button"
              className="mis-report-close-btn"
              onClick={closeLrStatusModal}
            >
              x
            </button>
            <div
              className={`mis-report-result-badge mis-report-result-${
                normalizedLrAutomationStatus === "FAILED" ||
                normalizedLrAutomationStatus === "COMPLETED_WITH_ERRORS"
                  ? "error"
                  : normalizedLrAutomationStatus === "ALREADY_RUNNING"
                    ? "info"
                  : normalizedLrAutomationStatus === "COMPLETED"
                    ? "success"
                    : "info"
              }`}
            >
              {normalizedLrAutomationStatus === "FAILED"
                ? "Failed"
                : normalizedLrAutomationStatus === "ALREADY_RUNNING"
                  ? "Running"
                : normalizedLrAutomationStatus === "COMPLETED" ||
                    normalizedLrAutomationStatus === "COMPLETED_WITH_ERRORS" ||
                    lrAutomationState.isComplete
                  ? "Completed"
                  : "Processing"}
            </div>
            <div className="mis-report-modal-title">LR Automation Status</div>
            <div className="mis-report-modal-subtitle">
              {lrAutomationState.message ||
                "We are processing your LR automation request. Please wait."}
            </div>

            <div className="mis-report-lr-status-content">
              <div className="mis-report-lr-status-grid">
                <div className="mis-report-lr-status-row">
                  <div className="mis-report-lr-status-label">Status</div>
                  <div className="mis-report-lr-status-value">
                    {normalizedLrAutomationStatus === "ALREADY_RUNNING"
                      ? "RUNNING"
                      : normalizedLrAutomationStatus}
                  </div>
                </div>
                <div className="mis-report-lr-status-row">
                  <div className="mis-report-lr-status-label">Total</div>
                  <div className="mis-report-lr-status-value">
                    {lrAutomationState.total}
                  </div>
                </div>
                <div className="mis-report-lr-status-row">
                  <div className="mis-report-lr-status-label">Processed</div>
                  <div className="mis-report-lr-status-value">
                    {lrAutomationState.processed}
                  </div>
                </div>
                <div className="mis-report-lr-status-row">
                  <div className="mis-report-lr-status-label">Success</div>
                  <div className="mis-report-lr-status-value">
                    {lrAutomationState.success}
                  </div>
                </div>
                <div className="mis-report-lr-status-row">
                  <div className="mis-report-lr-status-label">Failed</div>
                  <div className="mis-report-lr-status-value">
                    {lrAutomationState.failed}
                  </div>
                </div>
              </div>
            </div>

            {lrAutomationState.failed_records?.length > 0 && (
              <div className="mis-report-lr-error-section">
                <div className="mis-report-lr-error-title">Failed Records:</div>

                {lrAutomationState.failed_records.map((item, index) => (
                  <div key={index} className="mis-report-lr-error-item">
                    {item.raw}
                  </div>
                ))}
              </div>
            )}

            <div className="mis-report-modal-actions">
              {canStopLrAutomation && (
                <button
                  type="button"
                  className="mis-report-secondary-btn"
                  onClick={handleStopLrAutomation}
                  disabled={isStoppingLrAutomation}
                >
                  {isStoppingLrAutomation ? "Stopping..." : "Stop LR Automation"}
                </button>
              )}
              {lrReportUrl && (
                <button
                  type="button"
                  className="mis-report-secondary-btn"
                  onClick={openLrReport}
                >
                  View Report
                </button>
              )}
              <button
                type="button"
                className="mis-report-primary-btn"
                onClick={closeLrStatusModal}
              >
                {lrAutomationState.isComplete ? "Close" : "Hide"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isSoModalOpen && (
        <div className="mis-report-modal-overlay">
          <div className="mis-report-modal">
            <div className="mis-report-modal-title">Trigger SO Automation</div>
            <div className="mis-report-modal-subtitle">
              Upload the Excel file and select the date range for SO automation.
            </div>

            <label className="mis-report-field">
              <span>From Date</span>
              <input
                type="date"
                name="fromDate"
                value={soFilters.fromDate}
                onChange={handleSoFilterChange}
                disabled={isSoAutomationLoading}
              />
            </label>

            <label className="mis-report-field">
              <span>To Date</span>
              <input
                type="date"
                name="toDate"
                value={soFilters.toDate}
                onChange={handleSoFilterChange}
                disabled={isSoAutomationLoading}
              />
            </label>

            <label className="mis-report-field">
              <span>Excel File</span>
              <input
                type="file"
                name="file"
                accept=".xls,.xlsx"
                onChange={handleSoFilterChange}
                disabled={isSoAutomationLoading}
              />
            </label>

            <div className="mis-report-modal-actions">
              <button
                type="button"
                className="mis-report-secondary-btn"
                onClick={handleCloseSoModal}
                disabled={isSoAutomationLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="mis-report-primary-btn"
                onClick={handleTriggerSoAutomation}
                disabled={isSoAutomationLoading}
              >
                {isSoAutomationLoading ? "Processing..." : "Trigger SO"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isSoStatusModalOpen && (
        <div className="mis-report-modal-overlay">
          <div className="mis-report-modal mis-report-result-modal">
            <button
              type="button"
              className="mis-report-close-btn"
              onClick={closeSoStatusModal}
            >
              x
            </button>
            <div
              className={`mis-report-result-badge mis-report-result-${
                normalizedSoAutomationStatus === "FAILED" ||
                normalizedSoAutomationStatus === "ERROR"
                  ? "error"
                  : normalizedSoAutomationStatus === "SUCCESS"
                    ? "success"
                    : "info"
              }`}
            >
              {normalizedSoAutomationStatus === "FAILED" ||
              normalizedSoAutomationStatus === "ERROR"
                ? "Failed"
                : normalizedSoAutomationStatus === "SUCCESS" ||
                    soAutomationState.isComplete
                  ? "Completed"
                  : "Processing"}
            </div>
            <div className="mis-report-modal-title">SO Automation Status</div>
            <div className="mis-report-modal-subtitle">
              {soAutomationState.message ||
                "We are processing your SO automation request. Please wait."}
            </div>

            <div className="mis-report-lr-status-content">
              <div className="mis-report-lr-status-grid">
                <div className="mis-report-lr-status-row">
                  <div className="mis-report-lr-status-label">Status</div>
                  <div className="mis-report-lr-status-value">
                    {normalizedSoAutomationStatus}
                  </div>
                </div>
                <div className="mis-report-lr-status-row">
                  <div className="mis-report-lr-status-label">Total</div>
                  <div className="mis-report-lr-status-value">
                    {soAutomationState.total}
                  </div>
                </div>
                <div className="mis-report-lr-status-row">
                  <div className="mis-report-lr-status-label">Logs</div>
                  <div className="mis-report-lr-status-value">
                    {soAutomationState.logs.length}
                  </div>
                </div>
              </div>
            </div>

            {soAutomationState.logs.length > 0 && (
              <div className="mis-report-lr-error-section">
                <div className="mis-report-lr-error-title">Progress Log:</div>

                {soAutomationState.logs.map((item, index) => (
                  <div key={`${item}-${index}`} className="mis-report-lr-error-item">
                    {item}
                  </div>
                ))}
              </div>
            )}

            <div className="mis-report-modal-actions">
              <button
                type="button"
                className="mis-report-primary-btn"
                onClick={closeSoStatusModal}
              >
                {soAutomationState.isComplete ? "Close" : "Hide"}
              </button>
            </div>
          </div>
        </div>
      )}

      {String(sessionObject.sessionVariables.user_id) !== "277" && (
        <div className="notification-holder">
          <marquee
            className="notification-bar"
            behavior="scroll"
            direction="left"
            scrollamount="6"
          >
            {uniqueStations.map((station) => (
              <span key={station} className="station-item">
                {station}: {stationWeights[station]}
              </span>
            ))}
          </marquee>
        </div>
      )}

      {isMergeMisModalOpen && (
        <div className="mis-report-modal-overlay">
          <div className="mis-report-modal">
            <div className="mis-report-modal-title">
              Merge MIS Reports
            </div>

            <div className="mis-report-modal-subtitle">
              Upload the required files.
            </div>

            <label className="mis-report-field">
              <span>MIS Report File <span style={{ color: "red" }}>*</span></span>

              <input
                type="file"
                accept=".xls,.xlsx"
                onChange={(e) =>
                {  
                    console.log(e.target.files);

                    const file = e.target.files?.[0] || null;

                    console.log(file);

                  setMergeMisFiles((prev) => ({
                    ...prev,
                    misFile: file || null,
                  }))}
                }
                disabled={isMergeMisLoading}
              />
            </label>
              
            <label className="mis-report-field">
              <span>SAF File</span>
              
              <input
                type="file"
                accept=".xls,.xlsx"
                onChange={(e) =>


                  {
                    
                    const file = e.target.files?.[0] || null;
                     console.log("SAF file:", file)
                    setMergeMisFiles((prev) => ({
                    ...prev,
                    safFile: file || null,
                  }))}
                }
                disabled={isMergeMisLoading}
              />
            </label>
              
            <label className="mis-report-field">
              <span>RES File</span>
              
              <input
                type="file"
                accept=".xls,.xlsx"
                onChange={(e) =>
                {   

                  const file = e.target.files?.[0] || null;
                  console.log("RES file:", file)
                  setMergeMisFiles((prev) => ({
                    ...prev,
                    resFile: file || null,
                  }))}
                }
                disabled={isMergeMisLoading}
              />
            </label>
              
            <div className="mis-report-modal-actions">
              <button
                type="button"
                className="mis-report-secondary-btn"
                onClick={() => {
                  setMergeMisFiles({
                    misFile: null,
                    safFile: null,
                    resFile: null,
                  });
                
                  setIsMergeMisModalOpen(false);
                }}
                disabled={isMergeMisLoading}
              >
                Cancel
              </button>
              
              <button
                type="button"
                className="mis-report-primary-btn"
                onClick={handleMergeMisUpload}
                disabled={isMergeMisLoading}
              >
                {isMergeMisLoading ? "Uploading..." : "Upload"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
